/**
 * Focus-visible polyfill for better cross-browser support
 * Ensures focus indicators only show for keyboard navigation
 * Special handling for Safari which has aggressive :focus-visible behavior
 */

let hadKeyboardEvent = false;
let keyboardThrottleTimeout: number | null = null;

const FOCUS_VISIBLE_CLASS = 'focus-visible';

/**
 * Detect if we're running in Safari
 */
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

/**
 * Helper function to determine if focus should be visible
 */
function shouldShowFocusRing(target: Element): boolean {
  const { tagName, type } = target as HTMLInputElement;
  
  // Always show focus for form inputs (except certain types)
  if (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    (target as HTMLElement).contentEditable === 'true'
  ) {
    return !(type === 'range' || type === 'color' || type === 'checkbox' || type === 'radio');
  }
  
  // Show focus if element is focusable and keyboard was used
  return hadKeyboardEvent && target.matches(':focus');
}

/**
 * Force remove focus styles (Safari-specific)
 */
function forceRemoveFocusStyles(element: HTMLElement) {
  element.classList.remove(FOCUS_VISIBLE_CLASS);
  element.style.outline = 'none';
  element.style.boxShadow = 'none';
  element.style.borderColor = 'inherit';
  
  // Safari WebKit specific
  if (isSafari) {
    (element.style as any).webkitBoxShadow = 'none';
  }
}

/**
 * Force add focus styles (for keyboard navigation)
 */
function forceAddFocusStyles(element: HTMLElement) {
  element.classList.add(FOCUS_VISIBLE_CLASS);
  
  // Remove inline styles to let CSS take over
  element.style.removeProperty('outline');
  element.style.removeProperty('box-shadow');
  element.style.removeProperty('border-color');
  
  if (isSafari) {
    element.style.removeProperty('-webkit-box-shadow');
  }
  
  // Force Safari to apply the CSS focus styles
  if (isSafari) {
    // Trigger a reflow to ensure styles are applied
    element.offsetHeight;
  }
}

/**
 * Add focus-visible class when appropriate
 */
function onFocus(e: FocusEvent) {
  const target = e.target as HTMLElement;
  
  if (shouldShowFocusRing(target)) {
    forceAddFocusStyles(target);
    console.log('✅ Added focus-visible to:', target.tagName, 'keyboard mode:', hadKeyboardEvent);
  } else {
    forceRemoveFocusStyles(target);
    console.log('❌ Removed focus-visible from:', target.tagName, 'keyboard mode:', hadKeyboardEvent);
  }
}

/**
 * Remove focus-visible class
 */
function onBlur(e: FocusEvent) {
  const target = e.target as HTMLElement;
  forceRemoveFocusStyles(target);
}

/**
 * Track keyboard usage
 */
function onKeyDown(e: KeyboardEvent) {
  if (e.metaKey || e.altKey || e.ctrlKey) {
    return;
  }
  
  // Clear any pending timeout that would disable keyboard mode
  if (keyboardThrottleTimeout !== null) {
    clearTimeout(keyboardThrottleTimeout);
    keyboardThrottleTimeout = null;
  }
  
  console.log('⌨️ Keyboard detected:', e.key);
  hadKeyboardEvent = true;
}

/**
 * Track mouse/pointer usage - be more aggressive
 */
function onPointerDown() {
  console.log('🖱️ Mouse/pointer detected, disabling focus-visible');
  hadKeyboardEvent = false;
  
  // Remove focus-visible from currently focused element
  const activeElement = document.activeElement as HTMLElement;
  if (activeElement) {
    forceRemoveFocusStyles(activeElement);
    console.log('🚫 Removed focus-visible from:', activeElement.tagName);
  }
  
  // Don't set a timeout to re-enable keyboard mode
  // Let the next keyboard event handle it
  if (keyboardThrottleTimeout !== null) {
    clearTimeout(keyboardThrottleTimeout);
    keyboardThrottleTimeout = null;
  }
}

/**
 * Safari-specific click handler to immediately remove focus styles
 */
function onSafariClick(e: Event) {
  if (!isSafari) return;
  
  const target = e.target as HTMLElement;
  if (target && target.matches('button, a, [role="button"], [role="link"], [role="menuitem"], [role="tab"]')) {
    // Immediately remove focus styles on click
    forceRemoveFocusStyles(target);
    console.log('🍎 Safari click handler removed focus from:', target.tagName);
    
    // Also remove from any focused element
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement !== target) {
      forceRemoveFocusStyles(activeElement);
    }
  }
}

/**
 * Initialize the focus-visible polyfill
 */
export function initializeFocusVisible(): void {
  console.log('🔧 Initializing focus-visible polyfill, Safari detected:', isSafari);
  
  // Add class to document for CSS targeting
  document.documentElement.classList.add('js-focus-visible');
  
  // Add event listeners with capture to catch early
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('mousedown', onPointerDown, true);
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('touchstart', onPointerDown, true);
  document.addEventListener('click', onPointerDown, true);
  document.addEventListener('focus', onFocus, true);
  document.addEventListener('blur', onBlur, true);
  
  // Safari-specific click handler
  if (isSafari) {
    document.addEventListener('click', onSafariClick, true);
    document.addEventListener('mouseup', onSafariClick, true);
  }
  
  // Start with mouse mode (most common)
  hadKeyboardEvent = false;
}

/**
 * Cleanup function for the polyfill
 */
export function cleanupFocusVisible(): void {
  document.documentElement.classList.remove('js-focus-visible');
  document.removeEventListener('keydown', onKeyDown, true);
  document.removeEventListener('mousedown', onPointerDown, true);
  document.removeEventListener('pointerdown', onPointerDown, true);
  document.removeEventListener('touchstart', onPointerDown, true);
  document.removeEventListener('click', onPointerDown, true);
  document.removeEventListener('focus', onFocus, true);
  document.removeEventListener('blur', onBlur, true);
  
  if (isSafari) {
    document.removeEventListener('click', onSafariClick, true);
    document.removeEventListener('mouseup', onSafariClick, true);
  }
}